import React from 'react';
import { Button, Input } from 'reactstrap';
import classNames from 'classnames';

import Icon, { faChevronLeft, faChevronRight } from '~components/icon';
import Spinner from '~components/Spinner';

interface ComponentProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showCount?: boolean;
  showPageSizeOptions?: boolean;
  isLoading?: boolean;
}

const TablePagination: React.FC<ComponentProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showCount = true,
  showPageSizeOptions = true,
  isLoading = false,
}) => {
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number(e.target.value);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="table-pagination d-flex justify-content-between align-items-center p-2 mb-2">
      <div className="row-counter">
        {showCount && (
          <>
            Showing {startItem} - {endItem} of {totalCount} total rows
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </>
        )}
      </div>
      <div className="d-flex align-items-center gap-3">
        {showPageSizeOptions && onPageSizeChange && (
          <div className="pagination-info d-flex align-items-center gap-2">
            <Input
              type="select"
              value={pageSize}
              onChange={handlePageSizeChange}
              bsSize="sm"
              className="pagination-page-size"
              style={{ width: 'auto', border: 'none', padding: '0.25rem 1.5rem 0.25rem 0.5rem' }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
            <span className="text-muted small">per page</span>
          </div>
        )}
        {isLoading && <Spinner type="clip" className="me-2" loading={true} size={20} />}
        <div className="pagination-controls d-flex align-items-center gap-1">
          <Button
            size="sm"
            color=""
            disabled={currentPage === 1}
            onClick={handlePrevious}
            className="pagination-button p-1"
            style={{ minWidth: '2rem', border: 'none' }}
          >
            <Icon icon={faChevronLeft} />
          </Button>

          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-1 text-muted small">
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            const isActive = pageNum === currentPage;

            return (
              <Button
                key={pageNum}
                size="sm"
                color={isActive ? 'info' : ''}
                onClick={() => onPageChange(pageNum)}
                className={classNames('pagination-button p-1', { active: isActive })}
                style={{ minWidth: '2rem', border: 'none', fontWeight: isActive ? '600' : '400' }}
              >
                {pageNum}
              </Button>
            );
          })}

          <Button
            size="sm"
            color=""
            disabled={currentPage >= totalPages || totalPages === 0}
            onClick={handleNext}
            className="pagination-button p-1"
            style={{ minWidth: '2rem', border: 'none' }}
          >
            <Icon icon={faChevronRight} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
